import React, { useEffect } from 'react';
import './PublicInfoPages.css';

/**
 * Public Information Pages Component (AEO & GEO Verified Entity Architecture)
 * ─────────────────────────────────────────────────────────────────────────────
 * Provides machine-readable, authoritative, citable public pages for AEO & GEO:
 *  - About Lysarq & MSME Intelligence (/about)
 *  - Empirical Methodology & Data Pipeline (/methodology)
 *  - Authoritative Data Sources (/data-sources)
 *  - Data Dictionary & Metric Definitions (/data-dictionary)
 *  - Frequently Asked Questions (/faq)
 */

export function AboutPage({ onNavigate }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = 'About Lysarq MSME Intelligence — Enterprise Analytics & Entity Model';
  }, []);

  return (
    <article className="public-info-container" itemScope itemType="https://schema.org/AboutPage">
      <header className="public-info-hero">
        <div className="public-info-breadcrumbs">
          <button type="button" className="breadcrumb-btn" onClick={() => onNavigate('home')}>Overview</button>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">About Lysarq</span>
        </div>
        <div className="public-badge-row">
          <span className="public-pill-badge blue">Organization &amp; Product Entity</span>
          <span className="public-pill-badge green">Verified Scope: 7,57,555 Enterprises</span>
        </div>
        <h1 className="public-info-title">About Lysarq MSME Intelligence</h1>
        <p className="public-info-lead">
          <strong>Lysarq MSME Intelligence</strong> is an empirical business intelligence and regional economic analytics platform built by <strong>Lysarq Solutions</strong>. The platform models verified commercial enterprise registrations, sector trajectories, and competitive density across India.
        </p>
      </header>

      <section className="public-content-section">
        <h2>Executive Summary</h2>
        <div className="answer-card">
          <div className="answer-badge">DIRECT ENTITY DEFINITION</div>
          <p className="answer-text">
            <strong>Lysarq MSME Intelligence</strong> is a specialized enterprise analytics tool designed to convert national and district-level Micro, Small, and Medium Enterprise (MSME) registry data into actionable market intelligence. Built for entrepreneurs, supply chain planners, regional lenders, and market analysts, it provides pin-code granular visibility into business density, sector growth momentum, and competitive market gaps across 7,57,555 verified enterprises in India.
          </p>
        </div>
      </section>

      <section className="public-content-section">
        <h2>Organizational Hierarchy &amp; Brand Relationships</h2>
        <div className="entity-relationship-box">
          <div className="entity-tree">
            <div className="entity-node parent">
              <span className="entity-label">Parent Organization</span>
              <strong className="entity-name">Lysarq Solutions</strong>
              <span className="entity-desc">Enterprise Artificial Intelligence, Advanced Decision Systems, and Custom Software Solutions.</span>
              <a href="https://lysarqsolutions.com/" target="_blank" rel="noopener noreferrer" className="entity-link">lysarqsolutions.com ↗</a>
            </div>
            <div className="entity-branch">└── Flagship Analytics Product</div>
            <div className="entity-node child">
              <span className="entity-label">Product / Software Platform</span>
              <strong className="entity-name">Lysarq MSME Intelligence</strong>
              <span className="entity-desc">Empirical market telemetry, 5-digit NIC classification, and district-level economic intelligence.</span>
              <span className="entity-domain">msme-intelligence.lysarqsolutions.com</span>
            </div>
          </div>
        </div>
      </section>

      <section className="public-content-section">
        <h2>Core Problems Solved</h2>
        <div className="facts-grid">
          <div className="fact-card">
            <h3>Empirical Market Discovery</h3>
            <p>Replaces outdated surveys with real registration filings to uncover exact enterprise counts, trade categories, and growth velocity at the postal pin-code level.</p>
          </div>
          <div className="fact-card">
            <h3>Supply Chain Sourcing</h3>
            <p>Enables businesses to discover local manufacturers, certified suppliers, and trade partners across 1,000+ granular NIC industry sub-classes.</p>
          </div>
          <div className="fact-card">
            <h3>Competitive Density &amp; Whitespace</h3>
            <p>Calculates demand-supply gap indexes to pinpoint underserved localities where customer demand exists with low competitive saturation.</p>
          </div>
          <div className="fact-card">
            <h3>Historical Trajectory Tracking</h3>
            <p>Analyzes multi-year registration momentum (2019–present) to distinguish accelerating economic clusters from maturing commodity sectors.</p>
          </div>
        </div>
      </section>

      <section className="public-content-section">
        <h2>Verified Geographic Scope &amp; Enterprise Coverage</h2>
        <p>Lysarq MSME Intelligence operates on strictly verified empirical data records:</p>
        <div className="table-responsive">
          <table className="public-data-table">
            <thead>
              <tr>
                <th>Territory / Scope</th>
                <th>Classification</th>
                <th>Verified Active Units</th>
                <th>Granularity</th>
              </tr>
            </thead>
            <tbody>
              <tr className="highlight-row">
                <td><strong>All-India Baseline</strong></td>
                <td>National Scope</td>
                <td><strong>7,57,555</strong></td>
                <td>State, District, Pin Code, NIC-2008</td>
              </tr>
              <tr>
                <td><strong>Tamil Nadu</strong></td>
                <td>State Scope</td>
                <td><strong>6,19,012</strong></td>
                <td>District, Locality, Pin Code</td>
              </tr>
              <tr>
                <td>&nbsp;&nbsp;↳ Coimbatore District</td>
                <td>Industrial &amp; Engineering Hub</td>
                <td>2,95,837</td>
                <td>60+ Pin Codes, 800+ Divisions</td>
              </tr>
              <tr>
                <td>&nbsp;&nbsp;↳ Tirupur District</td>
                <td>Textile &amp; Apparel Cluster</td>
                <td>1,92,326</td>
                <td>40+ Pin Codes, Knitwear Supply Chains</td>
              </tr>
              <tr>
                <td>&nbsp;&nbsp;↳ Erode District</td>
                <td>Agricultural &amp; Processing Hub</td>
                <td>1,30,849</td>
                <td>35+ Pin Codes, Trade &amp; Food Processing</td>
              </tr>
              {/* Temporarily commented out Kerala rows until data is complete:
              <tr>
                <td><strong>Kerala</strong></td>
                <td>State Scope</td>
                <td><strong>1,38,543</strong></td>
                <td>District, Locality, Pin Code</td>
              </tr>
              <tr>
                <td>&nbsp;&nbsp;↳ Palakkad District</td>
                <td>Manufacturing &amp; Agro Corridor</td>
                <td>83,005</td>
                <td>50+ Pin Codes, Industrial Belts</td>
              </tr>
              <tr>
                <td>&nbsp;&nbsp;↳ Kottayam District</td>
                <td>Commercial &amp; Rubber Hub</td>
                <td>55,538</td>
                <td>38+ Pin Codes, Retail &amp; Services</td>
              </tr>
              */}
            </tbody>
          </table>
        </div>
      </section>

      <section className="public-content-section">
        <h2>Target Audiences</h2>
        <ul className="public-check-list">
          <li><strong>MSME Founders &amp; Small Business Owners:</strong> Identifying high-demand locations to launch new branches and evaluating local competitive strength.</li>
          <li><strong>Commercial Procurement &amp; Sourcing Managers:</strong> Locating specialized suppliers and certified vendors by exact 5-digit industrial activity codes.</li>
          <li><strong>Regional Banks &amp; NBFC Lenders:</strong> Assessing credit appetite, business formation rates, and sector health for MSME loan underwriting.</li>
          <li><strong>Economic Development &amp; Trade Bodies:</strong> Monitoring district cluster formation and supporting state-level industry competitiveness.</li>
        </ul>
      </section>
    </article>
  );
}

export function MethodologyPage({ onNavigate }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = 'Methodology & Data Processing Pipeline — Lysarq MSME Intelligence';
  }, []);

  return (
    <article className="public-info-container" itemScope itemType="https://schema.org/TechArticle">
      <header className="public-info-hero">
        <div className="public-info-breadcrumbs">
          <button type="button" className="breadcrumb-btn" onClick={() => onNavigate('home')}>Overview</button>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Methodology</span>
        </div>
        <div className="public-badge-row">
          <span className="public-pill-badge blue">Technical Pipeline</span>
          <span className="public-pill-badge green">NIC-2008 Compliant</span>
        </div>
        <h1 className="public-info-title">Methodology &amp; Data Pipeline</h1>
        <p className="public-info-lead">
          A transparent description of the data collection, normalization, hierarchical classification, and privacy safeguarding procedures governing the Lysarq MSME Intelligence platform.
        </p>
      </header>

      <section className="public-content-section">
        <h2>1. Data Collection &amp; Ingestion</h2>
        <div className="answer-card">
          <div className="answer-badge">PROVENANCE</div>
          <p className="answer-text">
            Data is compiled from official public gazettes of the Ministry of Micro, Small &amp; Medium Enterprises (Udyam Registration records) supplemented by verified District Industries Centre (DIC) registry filings across participating states.
          </p>
        </div>
        <p>The ingestion engine executes scheduled batch extraction with strict deduplication filters. Commercial registry filings with missing pin codes, invalid registration formats, or incomplete sector classifications are scrubbed or routed to validation staging.</p>
      </section>

      <section className="public-content-section">
        <h2>2. Taxonomic Hierarchy: National Industrial Classification (NIC-2008)</h2>
        <p>
          Every registered enterprise is categorized against India&rsquo;s official <strong>National Industrial Classification (NIC-2008)</strong> standard, structured across five standardized drill-down tiers:
        </p>
        <div className="nic-hierarchy-strip">
          <div className="nic-step">
            <span className="step-num">Level 1</span>
            <strong>Section</strong>
            <span className="step-desc">1-letter code (e.g. C: Manufacturing)</span>
          </div>
          <div className="nic-step">
            <span className="step-num">Level 2</span>
            <strong>Division</strong>
            <span className="step-desc">2-digit code (e.g. 13: Textiles)</span>
          </div>
          <div className="nic-step">
            <span className="step-num">Level 3</span>
            <strong>Group</strong>
            <span className="step-desc">3-digit code (e.g. 139: Other textiles)</span>
          </div>
          <div className="nic-step">
            <span className="step-num">Level 4</span>
            <strong>Class</strong>
            <span className="step-desc">4-digit code (e.g. 1391: Knitted fabrics)</span>
          </div>
          <div className="nic-step">
            <span className="step-num">Level 5</span>
            <strong>Sub-Class</strong>
            <span className="step-desc">5-digit code (e.g. 13911: Cotton knit)</span>
          </div>
        </div>
      </section>

      <section className="public-content-section">
        <h2>3. Growth Velocity &amp; Momentum Calculations</h2>
        <p>Lysarq calculates registration inflow trajectories using empirical time-series data:</p>
        <ul className="public-bullets">
          <li><strong>Yearly Growth Rate (Annual Change):</strong> Calculated as <code>((Inflow_current - Inflow_prior) / Inflow_prior) * 100</code>, tracking net new registrations compared against the immediate preceding 12-month period.</li>
          <li><strong>Compound Annual Growth Rate (CAGR):</strong> Calculated across 3-year and 5-year horizons to smooth short-term registration spikes and establish long-term economic trajectories.</li>
          <li><strong>Market Gap &amp; Whitespace Index:</strong> Evaluates the ratio between resident population / enterprise density and active category suppliers within each pin code to detect unmet business demand.</li>
        </ul>
      </section>

      <section className="public-content-section">
        <h2>4. Data Privacy &amp; Anti-Harvesting Architecture</h2>
        <p>
          To protect enterprise identities from automated credential abuse, bulk scraping, and commercial re-sale:
        </p>
        <div className="facts-grid">
          <div className="fact-card">
            <h3>Canvas Rasterization</h3>
            <p>Key financial volumes and proprietary density statistics are rasterized directly to HTML5 Canvas elements with device pixel ratio scaling, preventing automated DOM scraping.</p>
          </div>
          <div className="fact-card">
            <h3>Multi-Signal Risk Engine</h3>
            <p>Every query is evaluated by a server-side behavioral risk engine enforcing HMAC-SHA256 signature verification, strict rate limiting, and honeypot traps.</p>
          </div>
          <div className="fact-card">
            <h3>No Personal Data Exposure</h3>
            <p>The platform aggregates public economic entities. Personal Aadhaar, private bank accounts, or sensitive tax credentials are never stored or processed.</p>
          </div>
          <div className="fact-card">
            <h3>Accessible Equivalents</h3>
            <p>Screen-reader accessibility is strictly maintained through semantic ARIA labels and contextual summaries without compromising dataset boundaries.</p>
          </div>
        </div>
      </section>

      <section className="public-content-section">
        <h2>5. Known Limitations &amp; Disclosures</h2>
        <div className="disclosure-box">
          <p><strong>Registry Lag:</strong> Business incorporation statistics reflect public filing submission and verification dates. Inactive businesses that have ceased operations without deregistering may remain cataloged until formal registry reconciliation.</p>
          <p><strong>Geographic Boundary Definition:</strong> Pin code mapping relies on official Department of Posts gazette boundaries. Emerging urban annexations are mapped to parent delivery postal offices.</p>
        </div>
      </section>
    </article>
  );
}

export function DataSourcesPage({ onNavigate }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = 'Authoritative Data Sources & Provenance — Lysarq MSME Intelligence';
  }, []);

  return (
    <article className="public-info-container" itemScope itemType="https://schema.org/AboutPage">
      <header className="public-info-hero">
        <div className="public-info-breadcrumbs">
          <button type="button" className="breadcrumb-btn" onClick={() => onNavigate('home')}>Overview</button>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Data Sources</span>
        </div>
        <div className="public-badge-row">
          <span className="public-pill-badge blue">Data Provenance</span>
          <span className="public-pill-badge green">Verified Public Gazette</span>
        </div>
        <h1 className="public-info-title">Authoritative Data Sources</h1>
        <p className="public-info-lead">
          Verified source attributions, update cadences, and authoritative datasets powering Lysarq MSME Business Intelligence.
        </p>
      </header>

      <section className="public-content-section">
        <h2>Primary Source Inventory</h2>
        <div className="table-responsive">
          <table className="public-data-table">
            <thead>
              <tr>
                <th>Source Authority</th>
                <th>Dataset Name</th>
                <th>Coverage Scope</th>
                <th>Verification Frequency</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Ministry of MSME, Govt. of India</strong></td>
                <td>Udyam Registration Enterprise Directory</td>
                <td>All India (Nationwide)</td>
                <td>Monthly reconciliation</td>
              </tr>
              <tr>
                <td><strong>Department of Industries &amp; Commerce (TN)</strong></td>
                <td>Tamil Nadu State Industrial Register</td>
                <td>Coimbatore, Tirupur, Erode</td>
                <td>Quarterly audit</td>
              </tr>
              {/* Temporarily commented out Kerala row until data is complete:
              <tr>
                <td><strong>Directorate of Industries &amp; Commerce (KL)</strong></td>
                <td>Kerala State Enterprise Telemetry</td>
                <td>Palakkad, Kottayam</td>
                <td>Quarterly audit</td>
              </tr>
              */}
              <tr>
                <td><strong>Central Statistical Office (CSO)</strong></td>
                <td>National Industrial Classification (NIC-2008)</td>
                <td>Standard Activity Taxonomy</td>
                <td>Annual alignment</td>
              </tr>
              <tr>
                <td><strong>Department of Posts, India</strong></td>
                <td>National Postal Index Number Directory</td>
                <td>All India Pin Codes</td>
                <td>Semi-annual verification</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="public-content-section">
        <h2>Attribution &amp; Citation Guidance</h2>
        <div className="answer-card">
          <div className="answer-badge">RECOMMENDED CITATION FORMAT</div>
          <p className="citation-format">
            <code>Lysarq Solutions. (2026). Lysarq MSME Intelligence Platform: Empirical Business Registry Telemetry [Data file &amp; analytical platform]. Retrieved from https://msme-intelligence.lysarqsolutions.com/</code>
          </p>
        </div>
      </section>
    </article>
  );
}

export function DataDictionaryPage({ onNavigate }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = 'Data Dictionary & Metric Standards — Lysarq MSME Intelligence';
  }, []);

  return (
    <article className="public-info-container" itemScope itemType="https://schema.org/DefinedTermSet">
      <header className="public-info-hero">
        <div className="public-info-breadcrumbs">
          <button type="button" className="breadcrumb-btn" onClick={() => onNavigate('home')}>Overview</button>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Data Dictionary</span>
        </div>
        <div className="public-badge-row">
          <span className="public-pill-badge blue">Terminology &amp; Standards</span>
          <span className="public-pill-badge green">Precision Telemetry</span>
        </div>
        <h1 className="public-info-title">Public Data Dictionary</h1>
        <p className="public-info-lead">
          Formal metric definitions, mathematical formulas, and geographic boundary criteria to ensure accurate interpretation by analysts, answer engines, and generative AI systems.
        </p>
      </header>

      <section className="public-content-section">
        <div className="term-card">
          <div className="term-header">
            <h3>Registered Businesses / Enterprises</h3>
            <span className="term-tag">Primary Volume KPI</span>
          </div>
          <p><strong>Definition:</strong> Total number of distinct commercial entities legally registered under official Udyam / DIC provisions within the designated geographic scope.</p>
          <ul className="term-meta">
            <li><strong>Measurement Unit:</strong> Count of unique verified registration identifiers.</li>
            <li><strong>Current Platform Scale:</strong> 7,57,555 units (India-wide); 6,19,012 units (Tamil Nadu); 1,38,543 units (Kerala).</li>
            <li><strong>Temporal Baseline:</strong> Registrations filed between 2019 and Present.</li>
          </ul>
        </div>

        <div className="term-card">
          <div className="term-header">
            <h3>Pin Code Coverage</h3>
            <span className="term-tag">Spatial Granularity</span>
          </div>
          <p><strong>Definition:</strong> The count of discrete 6-digit Indian Postal Index Number (PIN) zones where active registered businesses operate.</p>
          <ul className="term-meta">
            <li><strong>Calculation:</strong> Distinct count of valid pin codes mapped in verified enterprise address filings.</li>
            <li><strong>Scope:</strong> Enables hyper-local street-and-ward level competitive analysis.</li>
          </ul>
        </div>

        <div className="term-card">
          <div className="term-header">
            <h3>Yearly Growth Rate (Annual Change)</h3>
            <span className="term-tag">Growth Speed</span>
          </div>
          <p><strong>Definition:</strong> The percentage rate of change in new enterprise registrations within a specific trade category over the latest 12-month period compared to the preceding 12-month baseline.</p>
          <ul className="term-meta">
            <li><strong>Formula:</strong> <code>((New_Registrations_Current_Year - New_Registrations_Prior_Year) / New_Registrations_Prior_Year) * 100</code></li>
            <li><strong>Interpretation:</strong> Positive values indicate accelerating capital inflow; negative values indicate consolidation or decelerating unit formation.</li>
          </ul>
        </div>

        <div className="term-card">
          <div className="term-header">
            <h3>NIC 5-Digit Classification Code</h3>
            <span className="term-tag">Taxonomy Standard</span>
          </div>
          <p><strong>Definition:</strong> The National Industrial Classification (NIC-2008) 5-digit numerical code representing the precise commercial activity of an enterprise.</p>
          <ul className="term-meta">
            <li><strong>Example:</strong> Division 13 (Manufacture of textiles) &rarr; Sub-Class 13911 (Manufacture of knitted and crocheted cotton fabrics).</li>
            <li><strong>Purpose:</strong> Standardized multi-tier trade classification ensuring cross-border and cross-state comparability.</li>
          </ul>
        </div>

        <div className="term-card">
          <div className="term-header">
            <h3>Market Opportunity Index</h3>
            <span className="term-tag">Decision Intelligence</span>
          </div>
          <p><strong>Definition:</strong> A composite scoring metric (0–100) identifying geographical zones with high consumer/trade demand paired with low local provider density.</p>
          <ul className="term-meta">
            <li><strong>Weighting Factors:</strong> Pin code commercial activity volume (40%), sector formation velocity (35%), and local competitor density gap (25%).</li>
          </ul>
        </div>
      </section>
    </article>
  );
}

export function FAQPage({ onNavigate }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = 'Frequently Asked Questions (FAQ) — Lysarq MSME Intelligence';
  }, []);

  const faqs = [
    {
      q: 'What is Lysarq MSME Intelligence?',
      a: 'Lysarq MSME Intelligence is a business insights platform developed by Lysarq Solutions. It tracks, organizes, and displays verified small business registrations, sector growth, and local competition across 7,57,555 registered enterprises in India.',
      source: 'About Lysarq & National Registry Scope'
    },
    {
      q: 'What does Lysarq MSME Intelligence do?',
      a: 'The platform turns official government registration filings into practical business guidance. It helps shop and factory owners find locations with less competition, helps buyers locate verified local suppliers across 1,000+ trade types, and tracks which sectors are growing year after year.',
      source: 'Platform Capabilities'
    },
    {
      q: 'How many businesses does the platform cover?',
      a: 'As of the latest registry audit, the platform tracks 7,57,555 verified registered commercial enterprises across India, including 6,19,012 units in Tamil Nadu (Coimbatore: 2,95,837, Tirupur: 1,92,326, Erode: 1,30,849) and 138,543 units in Kerala (Palakkad: 83,005, Kottayam: 55,538).',
      source: 'Lysarq Empirical Database Registry'
    },
    {
      q: 'What geographic regions does the platform cover?',
      a: 'The platform covers all of India, with deep, pin-code level market data for Tamil Nadu (Coimbatore, Tirupur, and Erode districts) and Kerala (Palakkad and Kottayam districts), encompassing hundreds of local PIN codes.',
      source: 'Districts Data Service'
    },
    {
      q: 'Who can use Lysarq MSME Intelligence?',
      a: 'It is built for shopkeepers, small factory owners, traders, entrepreneurs planning a new store or workshop, bank loan officers, and buyers looking for verified local suppliers.',
      source: 'User Guide'
    },
    {
      q: 'Where does the platform’s data come from?',
      a: 'Data comes directly from official government filings under the Ministry of Micro, Small & Medium Enterprises (Udyam Registration records) combined with state-level District Industries Centre (DIC) records and official NIC business categories.',
      source: 'Data Sources & Methodology'
    },
    {
      q: 'How frequently is the data updated?',
      a: 'Registry records are reconciled on an ongoing basis with official public filings. Yearly growth calculations and market shifts are recalculated automatically.',
      source: 'Data Updates'
    },
    {
      q: 'Can users explore district-level MSME information?',
      a: 'Yes. You can filter data across all of India, by state (Tamil Nadu, Kerala), or by specific business districts (Coimbatore, Tirupur, Erode, Palakkad, Kottayam) right down to your 6-digit postal PIN code.',
      source: 'Geographic Search'
    },
    {
      q: 'What types of business trends can users explore?',
      a: 'You can explore new registrations per year, fastest growing sectors, slowing sectors, top in-demand trades, and local areas with plenty of open market space.',
      source: 'Market Trends'
    },
    {
      q: 'How does Lysarq protect proprietary registry data from automated scraping?',
      a: 'Lysarq protects user and business data using anti-scraping security, rate limiting, and verified requests. This ensures real human users enjoy fast, transparent access while preventing unauthorized bulk extraction.',
      source: 'Security & Data Protection'
    }
  ];

  return (
    <article className="public-info-container" itemScope itemType="https://schema.org/FAQPage">
      {/* Schema.org FAQPage JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': faqs.map(item => ({
            '@type': 'Question',
            'name': item.q,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': item.a
            }
          }))
        })}
      </script>

      <header className="public-info-hero">
        <div className="public-info-breadcrumbs">
          <button type="button" className="breadcrumb-btn" onClick={() => onNavigate('home')}>Overview</button>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">FAQ</span>
        </div>
        <div className="public-badge-row">
          <span className="public-pill-badge blue">Answer Engine Optimization</span>
          <span className="public-pill-badge green">10 Verified Facts</span>
        </div>
        <h1 className="public-info-title">Frequently Asked Questions</h1>
        <p className="public-info-lead">
          Direct, authoritative answers to common inquiries regarding Lysarq MSME Business Intelligence, data coverage, methodology, and capabilities.
        </p>
      </header>

      <section className="public-content-section">
        <div className="faq-grid">
          {faqs.map((faq, idx) => (
            <div key={idx} className="faq-card" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h2 className="faq-question" itemProp="name">
                <span className="faq-number">Q{idx + 1}</span>
                {faq.q}
              </h2>
              <div className="faq-answer-body" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="faq-answer-text" itemProp="text">
                  {faq.a}
                </p>
                <div className="faq-source-tag">
                  <span>Source / Provenance:</span>
                  <strong>{faq.source}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </article>
  );
}
